// =====================================================================
// apps/portal/src/main.js — Portal do inquilino.
//
// Quatro coisas, e nada além disso (superfície pequena = menos bug e
// menos risco):
//   1. cadastro do próprio inquilino (vira a base do contrato)
//   2. ver contrato e boletos/Pix
//   3. enviar comprovante de pagamento (foto)
//   4. abrir chamado com foto do problema
//
// O JS do painel da imobiliária NÃO existe neste bundle.
// =====================================================================

import { sb, slugDoHost, carregarBranding, aplicarTema, meuPerfil, api } from '../../../packages/ui/cliente.js';
import { montarSprite, icon, ICON_CSS } from '../../../packages/ui/icons.js';
import { escapeHtml, moeda, data, competencia, statusCobranca } from '../../../packages/ui/format.js';
import { iniciarTema, botaoTemaHtml, ligarTema, ativarOlhos } from '../../../packages/ui/tema.js';
import { ligarZoomFotos } from '../../../packages/ui/gestos.js';

const app = document.getElementById('app');
let PERFIL = null;
let TENANT = null;

// =====================================================================
// Rodapé — o que o candidato vê ANTES de entregar CPF, renda e documentos.
//
// "Em conformidade com a LGPD", e não "certificado": conformidade é o que
// a empresa declara e sustenta com o que faz — consentimento registrado,
// dado isolado, descarte no prazo, encarregado nomeado. O sistema entrega
// as quatro coisas, então a frase é verdadeira.
// =====================================================================
function rodape() {
  const seguro = location.protocol === 'https:' || location.hostname === 'localhost';
  return `
    <footer class="rodape">
      <div class="selos">
        <span class="selo ${seguro ? 'ok' : 'atencao'}">${icon('cadeado')}
          <span class="stxt"><b>${seguro ? 'Site seguro' : 'Conexão sem HTTPS'}</b>
            <i>${seguro ? 'Conexão SSL' : 'certificado em emissão'}</i></span></span>
        <button class="selo ok" id="ver-politica">${icon('seguranca')}
          <span class="stxt"><b>LGPD</b><i>Lei 13.709/2018</i></span></button>
        ${botaoTemaHtml('selo')}
      </div>
      <small>Smart Gestão Imobiliária</small>
    </footer>`;
}

async function abrirPolitica() {
  const { data: pol } = await sb.from('politicas')
    .select('titulo,corpo,versao').eq('tipo', 'privacidade').eq('vigente', true).maybeSingle();
  const f = document.createElement('div');
  f.className = 'modal-fundo';
  f.innerHTML = `
    <div class="modal">
      <header><h3>${escapeHtml(pol?.titulo || 'Política de Privacidade')}</h3>
        <button class="icone" data-fechar>${icon('erro')}</button></header>
      <div class="corpo">
        <div class="texto-legal">${escapeHtml(pol?.corpo || 'Política ainda não publicada.')}</div>
      </div>
      <footer><button data-fechar>Fechar</button></footer>
    </div>`;
  f.querySelectorAll('[data-fechar]').forEach(b => (b.onclick = () => f.remove()));
  f.onclick = (e) => { if (e.target === f) f.remove(); };
  document.body.appendChild(f);
}

// Chamado depois de cada render: liga o rodapé, o tema, o olho da senha e
// a pinça nas fotos. Fica num lugar só porque toda tela chama isto — e o
// que depende de cada tela lembrar de ligar é o que a próxima tela esquece.
function ligarRodape() {
  const b = document.getElementById('ver-politica');
  if (b) b.onclick = abrirPolitica;
  ligarTema();
  ativarOlhos();
  ligarZoomFotos();   // o inquilino amplia a foto do chamado e do comprovante
}

// ─── Boot ─────────────────────────────────────────────────────────────
async function boot({ comSplash = false } = {}) {
  iniciarTema();     // antes de pintar: trocar de tema com a tela pronta pisca
  montarSprite();

  // Splash da marca: uma vez por sessão. Ver a logo ao abrir é bom;
  // vê-la a cada 'sair' seria irritante.
  if (comSplash && !sessionStorage.getItem('splash_visto')) {
    sessionStorage.setItem('splash_visto', '1');
    app.innerHTML = `
      <div class="splash">
        <img src="/logo-splash.png" alt="Smart Gestão Imobiliária">
        <div class="nome">Smart Gestão Imobiliária</div>
        <div class="barra"><i></i></div>
      </div>`;
    await new Promise(r => setTimeout(r, 3000));
  }
  const style = document.createElement('style');
  style.textContent = ICON_CSS;
  document.head.appendChild(style);

  const { slug } = slugDoHost();
  TENANT = await carregarBranding(slug);
  aplicarTema(TENANT);

  const { data: { session } } = await sb.auth.getSession();
  if (!session) return telaLogin();

  PERFIL = await meuPerfil();
  if (!PERFIL) return telaLogin('Perfil não encontrado. Fale com a administradora.');
  if (!PERFIL.aprovado) return telaAguardandoAprovacao();

  telaPrincipal();
}

// ─── Login / cadastro ─────────────────────────────────────────────────
function telaLogin(erro = '') {
  app.innerHTML = `
    <div class="auth">
      <img class="logo" src="/logo-topo.png" alt="">
      <h1>${escapeHtml(TENANT?.nome || 'Portal do Inquilino')}</h1>
      <p class="sub">Portal do inquilino</p>
      ${erro ? `<div class="erro">${icon('erro')} ${escapeHtml(erro)}</div>` : ''}
      <form id="f-login">
        <label>E-mail<input type="email" name="email" required autocomplete="username"></label>
        <label>Senha<input type="password" name="senha" required autocomplete="current-password"></label>
        <button class="primaria" type="submit">${icon('chave')} Entrar</button>
      </form>
      <button class="link" id="btn-cadastro">Ainda não tenho cadastro</button>
      <button class="link" id="btn-esqueci">Esqueci minha senha</button>
    </div>
    ${rodape()}`;
  ligarRodape();

  document.getElementById('f-login').onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const { error } = await sb.auth.signInWithPassword({
      email: fd.get('email'),
      password: fd.get('senha')
    });
    // Mensagem genérica de propósito: não revela se o e-mail existe.
    if (error) return telaLogin('E-mail ou senha inválidos');
    boot({ comSplash: true });
  };
  document.getElementById('btn-cadastro').onclick = telaCadastro;
  document.getElementById('btn-esqueci').onclick = async () => {
    const email = prompt('Informe seu e-mail:');
    if (!email) return;
    await sb.auth.resetPasswordForEmail(email, { redirectTo: location.origin });
    alert('Se o e-mail estiver cadastrado, enviamos o link de redefinição.');
  };
}

// Autocadastro do candidato. Cria o login, a ficha ('pre_cadastro') e —
// o mais importante — o REGISTRO DE CONSENTIMENTO, apontando a versão da
// política que ele leu. Sem aceite, o cadastro nem começa: não existe base
// legal para tratar o dado dele.
async function telaCadastro() {
  const { slug } = slugDoHost();

  // Carrega o texto vigente. Se não houver política publicada, não há
  // consentimento válido possível — e é melhor não cadastrar.
  const { data: pol } = await sb
    .from('politicas')
    .select('titulo,corpo,versao')
    .eq('tipo', 'consentimento_cadastro')
    .eq('vigente', true)
    .maybeSingle();

  app.innerHTML = `
    <div class="auth" style="max-width:520px">
      <h1>Cadastro do candidato</h1>
      <p class="sub">${escapeHtml(TENANT?.nome || '')}</p>
      ${!pol ? `<div class="erro">${icon('erro')} Cadastro indisponível: a política de
        privacidade ainda não foi publicada por esta administradora.</div>` : ''}
      <form id="f-cad" ${pol ? '' : 'style="display:none"'}>
        <label>Nome completo<input name="nome" required></label>
        <label>CPF<input name="cpf" required inputmode="numeric"></label>
        <label>Identidade (RG ou CNH)<input name="rg"></label>
        <label>Data de nascimento<input name="nascimento" type="date" required></label>
        <label>Telefone com DDD (WhatsApp)<input name="telefone" required inputmode="tel"></label>
        <label>E-mail<input name="email" type="email" required></label>
        <label>Estado civil
          <select name="estado_civil" id="ec">
            <option value="">—</option>
            <option value="solteiro">Solteiro(a)</option>
            <option value="casado">Casado(a)</option>
            <option value="uniao_estavel">União estável</option>
            <option value="divorciado">Divorciado(a)</option>
            <option value="viuvo">Viúvo(a)</option>
          </select>
        </label>

        <fieldset id="conj" style="display:none;border:1px solid var(--borda);
                  border-radius:10px;padding:12px">
          <legend style="font-size:13px;color:var(--tinta-fraca)">Dados do cônjuge</legend>
          <label>Nome<input name="conjuge_nome"></label>
          <label>CPF<input name="conjuge_cpf"></label>
          <label>Identidade<input name="conjuge_rg"></label>
          <label>Telefone<input name="conjuge_telefone"></label>
          <label>Regime de bens<input name="regime_bens"></label>
        </fieldset>

        <label>Profissão<input name="profissao"></label>
        <label>Renda mensal<input name="renda" type="number" step="0.01"></label>

        <label class="upload">${icon('foto')} Sua foto
          <input type="file" id="foto" accept="image/*" capture="user" hidden>
        </label>
        <div id="previa-foto" class="previa"></div>

        <label>Crie uma senha<input name="senha" type="password" required minlength="10"
               autocomplete="new-password"></label>
        <small>Mínimo 10 caracteres, com maiúscula, minúscula, número e símbolo.</small>

        <div style="border:1px solid var(--borda);border-radius:10px;padding:12px;
                    max-height:180px;overflow:auto;font-size:13px;background:#fff;text-align:left">
          <strong>${escapeHtml(pol?.titulo || '')}</strong>
          <p style="white-space:pre-wrap;color:var(--tinta-fraca)">${escapeHtml(pol?.corpo || '')}</p>
        </div>
        <label style="flex-direction:row;align-items:flex-start;gap:10px;font-weight:400">
          <input type="checkbox" name="consentimento" required style="width:auto;margin-top:3px">
          <span>Li e aceito a política acima (versão ${escapeHtml(pol?.versao || '')}).
          Autorizo o tratamento dos meus dados para análise de locação.</span>
        </label>

        <button class="primaria" type="submit">${icon('ok')} Enviar cadastro</button>
      </form>
      <button class="link" id="btn-voltar">${icon('voltar')} Voltar</button>
    </div>
    ${rodape()}`;

  ligarRodape();
  document.getElementById('btn-voltar').onclick = () => telaLogin();
  if (!pol) return;

  // Cônjuge só aparece se casado — pedir dado que não se usa é violar a
  // minimização da LGPD, e é fricção à toa no formulário.
  const ec = document.getElementById('ec');
  ec.onchange = () => {
    document.getElementById('conj').style.display =
      ['casado', 'uniao_estavel'].includes(ec.value) ? 'block' : 'none';
  };

  let fotoFile = null;
  const inputFoto = document.getElementById('foto');
  inputFoto.onchange = () => {
    fotoFile = inputFoto.files[0] || null;
    document.getElementById('previa-foto').innerHTML = fotoFile
      ? `<img src="${URL.createObjectURL(fotoFile)}" alt="">` : '';
  };

  document.getElementById('f-cad').onsubmit = async (e) => {
    e.preventDefault();
    const btn = e.target.querySelector('button[type=submit]');
    btn.disabled = true;
    btn.textContent = 'Enviando…';
    const fd = Object.fromEntries(new FormData(e.target));

    try {
      const r = await fetch('/.netlify/functions/portal-cadastro', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...fd,
          consentimento: true,
          tenant_slug: slug
        })
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'Falha no cadastro');

      // A foto sobe depois do login existir — antes disso não há sessão,
      // e o Storage exige usuário autenticado pra gravar no tenant certo.
      if (fotoFile) {
        await sb.auth.signInWithPassword({ email: fd.email, password: fd.senha });
        const p = await sb.from('perfis').select('id,tenant_id').maybeSingle();
        if (p.data) {
          const path = `${p.data.tenant_id}/candidatos/${p.data.id}.jpg`;
          await sb.storage.from('documentos').upload(path, fotoFile, { upsert: true });
          const { data: inq } = await sb.from('inquilinos').select('id').eq('user_id', p.data.id).maybeSingle();
          if (inq) await sb.from('inquilinos').update({ foto_url: path }).eq('id', inq.id);
        }
        await sb.auth.signOut();
      }

      app.innerHTML = `<div class="auth" style="text-align:center">
        <h1>${icon('ok', 'ic-xl')}</h1>
        <p>${escapeHtml(j.mensagem)}</p>
        <p class="sub">Você recebe um WhatsApp quando o acesso for liberado.</p>
      </div>`;
    } catch (err) {
      btn.disabled = false;
      btn.textContent = 'Enviar cadastro';
      alert(err.message);
    }
  };
}

function telaAguardandoAprovacao() {
  app.innerHTML = `<div class="auth">
    <h1>${icon('alerta', 'ic-xl')}</h1>
    <p>Seu cadastro está em análise pela administradora.</p>
    <button class="link" id="sair">Sair</button></div>`;
  document.getElementById('sair').onclick = async () => { await sb.auth.signOut(); boot(); };
}

// ─── App logado ───────────────────────────────────────────────────────
function telaPrincipal() {
  app.innerHTML = `
    <header class="topo">
      <span>${escapeHtml(TENANT?.nome || '')}</span>
      <button class="icone" id="sair" title="Sair">${icon('sair')}</button>
    </header>
    <nav class="abas">
      <button data-aba="cobrancas" class="ativa">${icon('cobranca')} Aluguel</button>
      <button data-aba="chamados">${icon('chamado')} Chamados</button>
      <button data-aba="contrato">${icon('contrato')} Contrato</button>
    </nav>
    <main id="conteudo"></main>
    ${rodape()}`;

  ligarRodape();
  document.getElementById('sair').onclick = async () => { await sb.auth.signOut(); boot(); };
  document.querySelectorAll('.abas button').forEach(b => {
    b.onclick = () => {
      document.querySelectorAll('.abas button').forEach(x => x.classList.remove('ativa'));
      b.classList.add('ativa');
      ABAS[b.dataset.aba]();
    };
  });
  ABAS.cobrancas();
}

const ABAS = {
  // ── Aluguel: lista as cobranças e permite enviar o comprovante ──────
  async cobrancas() {
    const el = document.getElementById('conteudo');
    el.innerHTML = '<p class="carregando">Carregando…</p>';

    // A RLS já devolve SÓ as cobranças deste inquilino. Sem filtro no client.
    const { data: cobs, error } = await sb
      .from('cobrancas')
      .select('id,competencia,vencimento,valor_total,multa,juros,status,pix_copia_cola,comprovante_url')
      .order('competencia', { ascending: false })
      .limit(24);

    if (error) return (el.innerHTML = `<div class="erro">${escapeHtml(error.message)}</div>`);
    if (!cobs.length) return (el.innerHTML = '<p class="vazio">Nenhuma cobrança ainda.</p>');

    el.innerHTML = cobs.map(c => {
      const st = statusCobranca(c);
      const total = Number(c.valor_total) + Number(c.multa || 0) + Number(c.juros || 0);
      return `
      <article class="cartao">
        <div class="linha">
          <strong>${escapeHtml(competencia(c.competencia))}</strong>
          <span class="badge ${st.cor}">${escapeHtml(st.txt)}</span>
        </div>
        <div class="valor">${moeda(total)}</div>
        <div class="meta">Vence em ${data(c.vencimento)}</div>
        ${c.status !== 'paga' && c.pix_copia_cola ? `
          <button class="secundaria" data-pix="${escapeHtml(c.pix_copia_cola)}">
            ${icon('pix')} Copiar Pix
          </button>` : ''}
        ${c.status !== 'paga' ? `
          <label class="upload">
            ${icon('upload')} ${c.comprovante_url ? 'Reenviar comprovante' : 'Enviar comprovante'}
            <input type="file" accept="image/*" capture="environment" data-cob="${c.id}" hidden>
          </label>` : ''}
      </article>`;
    }).join('');

    el.querySelectorAll('[data-pix]').forEach(b => {
      b.onclick = () => {
        navigator.clipboard.writeText(b.dataset.pix);
        b.textContent = 'Pix copiado';
      };
    });
    el.querySelectorAll('input[data-cob]').forEach(i => {
      i.onchange = () => enviarComprovante(i.dataset.cob, i.files[0]);
    });
  },

  // ── Chamados: abre com foto ─────────────────────────────────────────
  async chamados() {
    const el = document.getElementById('conteudo');
    const { data: lista } = await sb
      .from('chamados')
      .select('id,numero,titulo,categoria,status,criado_em')
      .order('criado_em', { ascending: false });

    el.innerHTML = `
      <button class="primaria bloco" id="novo">${icon('novo')} Abrir chamado</button>
      ${(lista || []).map(c => `
        <article class="cartao">
          <div class="linha">
            <strong>#${c.numero} ${escapeHtml(c.titulo)}</strong>
            <span class="badge">${escapeHtml(c.status.replace('_', ' '))}</span>
          </div>
          <div class="meta">${escapeHtml(c.categoria)} · ${data(c.criado_em)}</div>
        </article>`).join('') || '<p class="vazio">Nenhum chamado aberto.</p>'}`;

    document.getElementById('novo').onclick = telaNovoChamado;
  },

  // ── Contrato ────────────────────────────────────────────────────────
  async contrato() {
    const el = document.getElementById('conteudo');
    const { data: cs } = await sb
      .from('contratos')
      .select('numero,data_inicio,data_fim,valor_aluguel,valor_condominio,dia_vencimento,'
            + 'indice_reajuste,documento_url,status,imoveis(codigo,logradouro,numero,bairro,cidade)')
      .eq('status', 'ativo');

    const c = cs && cs[0];
    if (!c) return (el.innerHTML = '<p class="vazio">Nenhum contrato ativo.</p>');
    const im = c.imoveis || {};
    el.innerHTML = `
      <article class="cartao">
        <div class="linha"><strong>Contrato ${escapeHtml(c.numero)}</strong></div>
        <div class="meta">
          ${escapeHtml(im.logradouro || '')}, ${escapeHtml(im.numero || '')} —
          ${escapeHtml(im.bairro || '')}, ${escapeHtml(im.cidade || '')}
        </div>
        <dl>
          <dt>Aluguel</dt><dd>${moeda(c.valor_aluguel)}</dd>
          <dt>Condomínio</dt><dd>${moeda(c.valor_condominio)}</dd>
          <dt>Vencimento</dt><dd>todo dia ${c.dia_vencimento}</dd>
          <dt>Vigência</dt><dd>${data(c.data_inicio)} a ${data(c.data_fim)}</dd>
          <dt>Reajuste</dt><dd>${escapeHtml(c.indice_reajuste)}</dd>
        </dl>
        ${c.documento_url
          ? `<a class="secundaria" href="${escapeHtml(c.documento_url)}" target="_blank" rel="noopener">
               ${icon('pdf')} Baixar contrato</a>`
          : ''}
      </article>`;
  }
};

// ─── Upload do comprovante ────────────────────────────────────────────
// O browser só GRAVA o arquivo. Quem lê o valor e decide a baixa é a
// function portal-comprovante (Claude Vision). Não existe campo "valor"
// que o inquilino possa mandar — o número sai da imagem.
async function enviarComprovante(cobrancaId, file) {
  if (!file) return;
  if (file.size > 10 * 1024 * 1024) return alert('Arquivo acima de 10 MB.');

  const ext  = (file.name.split('.').pop() || 'jpg').toLowerCase();
  const path = `${PERFIL.tenant_id}/${cobrancaId}/${Date.now()}.${ext}`;

  const { error } = await sb.storage.from('comprovantes').upload(path, file, { upsert: true });
  if (error) return alert('Falha ao enviar: ' + error.message);

  try {
    const r = await api('portal-comprovante', { cobranca_id: cobrancaId, path });
    alert(r.mensagem);
    ABAS.cobrancas();
  } catch (e) {
    alert(e.message);
  }
}

// ─── Novo chamado ─────────────────────────────────────────────────────
function telaNovoChamado() {
  const el = document.getElementById('conteudo');
  el.innerHTML = `
    <form id="f-cham" class="cartao">
      <label>O que houve?<input name="titulo" required maxlength="120"
             placeholder="Ex.: Infiltração no teto do banheiro"></label>
      <label>Onde?
        <select name="local">
          <option value="apartamento">No apartamento</option>
          <option value="condominio">No condomínio</option>
          <option value="area_comum">Em área comum</option>
        </select>
      </label>
      <label>Categoria
        <select name="categoria">
          <option value="hidraulica">Hidráulica</option>
          <option value="eletrica">Elétrica</option>
          <option value="infiltracao">Infiltração</option>
          <option value="estrutural">Estrutural</option>
          <option value="eletrodomestico">Eletrodoméstico</option>
          <option value="ar_condicionado">Ar-condicionado</option>
          <option value="chaves_fechadura">Chaves / fechadura</option>
          <option value="condominio">Condomínio</option>
          <option value="outros">Outros</option>
        </select>
      </label>
      <label>Detalhes<textarea name="descricao" rows="4"></textarea></label>
      <label class="upload">${icon('foto')} Tirar foto do problema
        <input type="file" name="fotos" accept="image/*" capture="environment" multiple hidden>
      </label>
      <div id="previa" class="previa"></div>
      <button class="primaria" type="submit">${icon('ok')} Abrir chamado</button>
    </form>`;

  const input = el.querySelector('input[name=fotos]');
  input.onchange = () => {
    document.getElementById('previa').innerHTML =
      Array.from(input.files).map(f =>
        `<img src="${URL.createObjectURL(f)}" alt="">`).join('');
  };

  el.querySelector('#f-cham').onsubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    try {
      const r = await api('portal-chamado', {
        titulo: fd.get('titulo'),
        local: fd.get('local'),
        categoria: fd.get('categoria'),
        descricao: fd.get('descricao')
      });
      // Fotos vão pro Storage depois que o chamado existe (precisamos do id).
      for (const f of input.files) {
        const path = `${PERFIL.tenant_id}/${r.chamado_id}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
        await sb.storage.from('chamados').upload(path, f);
        await sb.from('chamado_fotos').insert({
          tenant_id: PERFIL.tenant_id,
          chamado_id: r.chamado_id,
          url: path,
          etapa: 'abertura',
          enviado_por: PERFIL.id
        });
      }
      alert(`Chamado #${r.numero} aberto. Você será avisado pelo WhatsApp.`);
      ABAS.chamados();
    } catch (err) {
      alert(err.message);
    }
  };
}

boot({ comSplash: true });
